import axios from 'axios';

const PINTEREST_API_BASE_URL = 'https://api.pinterest.com/v5';
const PINTEREST_ACCESS_TOKEN = import.meta.env?.VITE_PINTEREST_ACCESS_TOKEN;

/**
 * Maps Pinterest API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Pinterest API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid Pinterest access token. Please check your API credentials.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your access token does not have the required permissions.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested board or pin may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Pinterest service error. Please try again later.' };
  } else {
    return { isInternal: false, message: errorData?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Searches Pinterest for pins related to interior design
 * @param {string} query - Search query (e.g., "modern bedroom interior")
 * @param {number} limit - Number of results to return (default: 25, max: 250)
 * @returns {Promise<Array>} - Array of pin objects with image URLs and metadata
 */
export async function searchPinterestPins(query, limit = 25) {
  if (!PINTEREST_ACCESS_TOKEN || PINTEREST_ACCESS_TOKEN === 'your-pinterest-access-token-here') {
    throw new Error('Pinterest access token is not configured. Please add your token to the .env file.');
  }

  if (!query?.trim()) {
    throw new Error('Please provide a search query.');
  }

  try {
    const response = await axios?.get(`${PINTEREST_API_BASE_URL}/search/pins`, {
      params: {
        query: query?.trim(),
        limit: Math.min(limit, 250)
      },
      headers: {
        'Authorization': `Bearer ${PINTEREST_ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    const pins = response?.data?.items || [];
    return pins?.map(pin => ({
      id: pin?.id,
      title: pin?.title || 'Untitled',
      description: pin?.description || '',
      imageUrl: pin?.media?.images?.['600x']?.url || pin?.media?.images?.original?.url,
      link: pin?.link || `https://www.pinterest.com/pin/${pin?.id}`,
      boardName: pin?.board?.name || '',
      creatorName: pin?.creator?.username || 'Unknown',
      alt: pin?.alt_text || pin?.title || 'Interior design inspiration from Pinterest'
    }));
  } catch (error) {
    if (error?.response) {
      const { status, data } = error?.response;
      const { isInternal, message } = getErrorMessage(status, data);
      throw new Error(message);
    } else if (error?.request) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else {
      throw new Error(error?.message || 'Failed to search Pinterest pins.');
    }
  }
}

/**
 * Gets user's Pinterest boards
 * @returns {Promise<Array>} - Array of board objects with metadata
 */
export async function getUserBoards() {
  if (!PINTEREST_ACCESS_TOKEN || PINTEREST_ACCESS_TOKEN === 'your-pinterest-access-token-here') {
    throw new Error('Pinterest access token is not configured. Please add your token to the .env file.');
  }

  try {
    const response = await axios?.get(`${PINTEREST_API_BASE_URL}/boards`, {
      headers: {
        'Authorization': `Bearer ${PINTEREST_ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    const boards = response?.data?.items || [];
    return boards?.map(board => ({
      id: board?.id,
      name: board?.name,
      description: board?.description || '',
      pinCount: board?.pin_count || 0,
      imageUrl: board?.media?.image_cover_url,
      privacy: board?.privacy || 'public'
    }));
  } catch (error) {
    if (error?.response) {
      const { status, data } = error?.response;
      const { isInternal, message } = getErrorMessage(status, data);
      throw new Error(message);
    } else if (error?.request) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else {
      throw new Error(error?.message || 'Failed to fetch Pinterest boards.');
    }
  }
}

/**
 * Gets pins from a specific board
 * @param {string} boardId - The Pinterest board ID
 * @param {number} limit - Number of pins to return (default: 25, max: 250)
 * @returns {Promise<Array>} - Array of pin objects from the board
 */
export async function getBoardPins(boardId, limit = 25) {
  if (!PINTEREST_ACCESS_TOKEN || PINTEREST_ACCESS_TOKEN === 'your-pinterest-access-token-here') {
    throw new Error('Pinterest access token is not configured. Please add your token to the .env file.');
  }

  if (!boardId) {
    throw new Error('Board ID is required.');
  }

  try {
    const response = await axios?.get(`${PINTEREST_API_BASE_URL}/boards/${boardId}/pins`, {
      params: {
        limit: Math.min(limit, 250)
      },
      headers: {
        'Authorization': `Bearer ${PINTEREST_ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    const pins = response?.data?.items || [];
    return pins?.map(pin => ({
      id: pin?.id,
      title: pin?.title || 'Untitled',
      description: pin?.description || '',
      imageUrl: pin?.media?.images?.['600x']?.url || pin?.media?.images?.original?.url,
      link: pin?.link || `https://www.pinterest.com/pin/${pin?.id}`,
      alt: pin?.alt_text || pin?.title || 'Interior design inspiration from Pinterest'
    }));
  } catch (error) {
    if (error?.response) {
      const { status, data } = error?.response;
      const { isInternal, message } = getErrorMessage(status, data);
      throw new Error(message);
    } else if (error?.request) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else {
      throw new Error(error?.message || 'Failed to fetch board pins.');
    }
  }
}

export default {
  searchPinterestPins,
  getUserBoards,
  getBoardPins
};